import { useCallback, useEffect, useState } from 'react';
import {
	brokerJobsStoreSelector,
	brokerServicesRepositorySingleton,
	MEZZ_PROXY_SERVICE_UUID,
	useBrokerJobsStore,
} from '@warehouse/object-browser/feature-broker';

/** `failed` is a job that could not be submitted; `jobFailed` is one that ran and did not succeed. */
export type RegenerateProxyStatus = 'idle' | 'submitting' | 'submitted' | 'failed' | 'jobFailed';

/**
 * Submits a Mezz Proxy job and follows it until it settles, so a job that fails can be retried. The broker jobs
 * store refetches the object once that job succeeds.
 */
export function useRegenerateProxy({ nxObjectUuid }: { nxObjectUuid: string }) {
	const [status, setStatus] = useState<RegenerateProxyStatus>('idle');
	const [submittedJobId, setSubmittedJobId] = useState<string | undefined>(undefined);
	const { triggerService } = useBrokerJobsStore(brokerJobsStoreSelector.actions);
	const jobs = useBrokerJobsStore(brokerJobsStoreSelector.jobsFor(nxObjectUuid));
	const submittedJobStatus = jobs.find((job) => job.jobId === submittedJobId)?.status;

	useEffect(() => {
		setStatus('idle');
		setSubmittedJobId(undefined);
	}, [nxObjectUuid]);

	useEffect(() => {
		if (status !== 'submitted') return;
		if (submittedJobStatus === 'SUCCEEDED') setStatus('idle');
		if (submittedJobStatus === 'FAILED' || submittedJobStatus === 'CANCELLED') setStatus('jobFailed');
	}, [status, submittedJobStatus]);

	const regenerate = useCallback(async () => {
		const mezzProxy = brokerServicesRepositorySingleton
			.get()
			.getAvailableServicesConfig()
			.find((service) => service.serviceUUID === MEZZ_PROXY_SERVICE_UUID);
		if (!mezzProxy) {
			setStatus('failed');
			return;
		}

		setStatus('submitting');
		// The store reports a submit it could not make as this jobId rather than throwing.
		const { jobId } = await triggerService(nxObjectUuid, mezzProxy);
		if (jobId === 'error') {
			setStatus('failed');
			return;
		}
		setSubmittedJobId(jobId);
		setStatus('submitted');
	}, [nxObjectUuid, triggerService]);

	return { status, regenerate };
}
